import { ShiftManagementStatsQuery } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import { stringToDate } from 'modules/core/lib/dates'

import type RestDay from 'modules/shift_management/store/internal/models/RestDay'

import type Leave from './Leave'

export type TShiftConflict =
  ShiftManagementStatsQuery['shiftManagement']['statsConnection']['nodes'][number]['shiftConflicts'][number]

export interface Props {
  id: string
  employee_id: string
  start_at: string
  local_start_at: string
  conflicting_shifts_ids: string[]
  conflicting_leaves_ids: string[]
  conflicting_rest_days_ids: string[]
}

class ShiftConflict {
  id: string
  employeeId: string
  startAt: moment.Moment
  localStartAt: moment.Moment
  conflictingShiftsIds: string[]
  conflictingLeavesIds: string[]
  conflictingRestDaysIds: string[]

  constructor(props: Props) {
    this.id = props.id
    this.employeeId = props.employee_id
    this.startAt = stringToDate(props.start_at, 'backend_datetime_without_timezone')
    this.localStartAt = stringToDate(props.local_start_at, 'backend_datetime_without_timezone')
    this.conflictingShiftsIds = props.conflicting_shifts_ids
    this.conflictingLeavesIds = props.conflicting_leaves_ids
    this.conflictingRestDaysIds = props.conflicting_rest_days_ids

    makeObservable(this, {
      hasLeaveConflict: computed,
      hasRestDayConflict: computed,
      _conflictingLeavesIdsSet: computed,
      _conflictingRestDaysIdsSet: computed,
    })
  }

  static buildFromGraphQL(conflict: TShiftConflict): ShiftConflict {
    return new ShiftConflict({
      id: conflict?.id,
      employee_id: conflict?.employeeId,
      start_at: conflict.startAt,
      local_start_at: conflict.localStartAt,
      conflicting_shifts_ids: conflict.conflictingShiftsIds,
      conflicting_leaves_ids: conflict.conflictingLeavesIds,
      conflicting_rest_days_ids: conflict.conflictingRestDaysIds,
    })
  }

  get hasLeaveConflict(): boolean {
    return !!this.conflictingLeavesIds.length
  }

  get hasRestDayConflict(): boolean {
    return !!this.conflictingRestDaysIds.length
  }

  get _conflictingLeavesIdsSet(): Set<string> {
    return new Set(this.conflictingLeavesIds)
  }

  get _conflictingRestDaysIdsSet(): Set<string> {
    return new Set(this.conflictingRestDaysIds)
  }

  hasConflictWithLeave(leave: Leave): boolean {
    return this._conflictingLeavesIdsSet.has(leave.id)
  }

  hasConflictWithRestDay(restDay: RestDay): boolean {
    return this._conflictingRestDaysIdsSet.has(restDay.id)
  }
}

export default ShiftConflict
